import { create } from "zustand";
import { dateTodayApiFormat } from "../../../utils/dateResolvers";
import { createJSONStorage, persist } from "zustand/middleware";
import globalConstant from "../../../utils/constants";

export interface IFilter {
  filterObject: {
    limit: number;
    orderStatus: string;
    supplierOrderStatus: string;
    customerType: string;
    orderHandler: string;
    filterOrderNo: string;
    filterOrderNoCheckBoxValue: boolean;
    customerId: string;
    customerName: string;
    orderType: string;
    transactionType: string;
    invoiceStatus: string;
    isSupplierInvoicePresent: boolean | undefined;
    startDate: string;
    endDate: string;
    states: string;
    deliveryMode: string;
    pendingOrderItems: string;
    voidedStatus: string;
    includeTestOrders: boolean;
    carMaker: string;
  };

  setFilterObject: (newFilterObject: any) => void;
  updateOrderHandlers: (orderHandlers: string[]) => void;
}

/** Default OH list query statuses (keep in sync with SideFilterTab orderStatuses). */
export const DEFAULT_ORDER_STATUS_FILTER =
  `&orderStatus=CBO&orderStatus=CANCELLED&orderStatus=ACK&orderStatus=ALLOCATED&orderStatus=READY_TO_DISPATCH&orderStatus=DISPATCHED&orderStatus=SHIPPED&orderStatus=BLOCKED&orderStatus=NEW&orderStatus=RETURN_REQUESTED&orderStatus=CUSTOMER_RETURNED&orderStatus=CUSTOMER_RETURN_REJECTED&orderStatus=SUPPLIER_RETURNED&orderStatus=SUPPLIER_RETURN_REJECTED&orderStatus=DELIVERED&orderStatus=ACCEPTED&orderStatus=REJECTED&orderStatus=CUSTOMER_HOLD`;

/**
 * Bump when default orderStatus gains new statuses so persisted localStorage
 * filters are migrated once for existing users.
 */
const ORDER_FILTER_PERSIST_VERSION = 1;

const ORDER_STATUSES_ADDED_IN_V1 = ["DISPATCHED", "SHIPPED"] as const;

const ensureOrderStatusesInFilter = (
  orderStatusQuery: string,
  statusesToEnsure: readonly string[],
): string => {
  let result = orderStatusQuery || "";
  const missing = statusesToEnsure.filter(
    (status) => !result.includes(`orderStatus=${status}`),
  );
  if (missing.length === 0) {
    return result;
  }

  const insert = missing.map((status) => `&orderStatus=${status}`).join("");
  const afterReady = "orderStatus=READY_TO_DISPATCH";
  if (result.includes(afterReady)) {
    return result.replace(afterReady, `${afterReady}${insert}`);
  }
  if (result) {
    return `${result}${insert}`;
  }
  return insert;
};

const useFiltersStore = create<IFilter>()(
  persist(
    (set, get) => ({
      filterObject: {
        limit: 20,
        orderStatus: DEFAULT_ORDER_STATUS_FILTER,
        supplierOrderStatus: `&supplierOrderStatus=NEW&supplierOrderStatus=CBO&supplierOrderStatus=CANCELLED&supplierOrderStatus=ACK&supplierOrderStatus=PICKED&supplierOrderStatus=READY&supplierOrderStatus=IN_REVIEW&supplierOrderStatus=REVOKED&supplierOrderStatus=NONE&supplierOrderStatus=RETURN_REQUESTED&supplierOrderStatus=ACCEPTED&supplierOrderStatus=REJECTED`,
        customerType: "",
        filterOrderNoCheckBoxValue: false,
        filterOrderNo: "",
        orderHandler: "",
        customerId: "",
        customerName: "",
        orderType: "&orderType=REGULAR&orderType=RETURN&orderType=SUBSCRIPTION",
        transactionType:
          "&transactionType=Trading&transactionType=Facilitation&transactionType=NONE",
        invoiceStatus:
          "&invoiceStatus=PAID&invoiceStatus=UNPAID&invoiceStatus=RECEIVED",
        isSupplierInvoicePresent: undefined,
        startDate: dateTodayApiFormat,
        endDate: dateTodayApiFormat,
        states: "",
        deliveryMode: "&deliveryMode=DOOR_DELIVERY&deliveryMode=SELF_PICKUP",
        pendingOrderItems: "",
        voidedStatus: "",
        includeTestOrders: false,
        carMaker: "",
      },
      currentLoggedInUserId: "",
      setFilterObject: (newFilterObject: any) => {
        set({
          filterObject: newFilterObject,
        });
      },
      updateOrderHandlers: (orderHandlers: string[]) => {
        set((state) => ({
          filterObject: {
            ...state.filterObject,
            orderHandler: ["", ...orderHandlers].join("&orderHandlerId="),
          },
        }));
      },
    }),

    {
      name: globalConstant.ORDER_FILTER_SETTINGS,
      storage: createJSONStorage(() => localStorage),
      version: ORDER_FILTER_PERSIST_VERSION,
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as IFilter | undefined;
        if (!state?.filterObject) {
          return persistedState as IFilter;
        }

        if (version < 1) {
          return {
            ...state,
            filterObject: {
              ...state.filterObject,
              orderStatus: ensureOrderStatusesInFilter(
                state.filterObject.orderStatus,
                ORDER_STATUSES_ADDED_IN_V1,
              ),
            },
          };
        }

        return state;
      },
    },
  ),
);

export default useFiltersStore;
